import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Modal,
  Button,
  Space,
  Typography,
  message,
  Tag,
  Spin,
  Steps,
  Collapse,
  Alert,
  Card,
  Tabs,
  Input,
  InputNumber,
  Select,
  Popconfirm,
  Empty,
  Slider,
  Switch,
} from 'antd';
import {
  AudioOutlined,
  ClockCircleOutlined,
  ThunderboltOutlined,
  VideoCameraOutlined,
  SaveOutlined,
  SoundOutlined,
  CustomerServiceOutlined,
  PlusOutlined,
  DeleteOutlined,
  LoadingOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { SourceEpisode, AudioAsset } from '../../types';
import { sourceEpisodesApi } from '../../services/api/sourceEpisodes';
import { audioLibraryApi } from '../../services/audioLibraryApi';
import { useApi } from '../../hooks';
import { SimpleVideoPlayerHandle } from '../../features/video-editor/components/SimpleVideoPlayer';
import { IsolatedVideoPlayer } from '../../features/video-editor/components/IsolatedVideoPlayer';
import type { Panel as VideoEditorPanel } from '../../features/video-editor/types';
import { PanelsGrid } from '../../components/PanelsGrid';
import { TimelineRuler } from '../../components/TimelineRuler';
import { EnhancedPlayerControls } from '../../components/EnhancedPlayerControls';
import { ProfessionalTimeline } from '../../components/ProfessionalTimeline';
import { FocusPointPicker } from '../../components/FocusPointPicker';
import { SubtitleEditorPanel } from '../../components/SubtitleEditorPanel';

const { Text, Title } = Typography;
const { Panel: CollapsePanel } = Collapse;
const { TabPane } = Tabs;
const { Option } = Select;

interface VideoEditorModalProps {
  visible: boolean;
  episode: SourceEpisode;
  storyId: string;
  onClose: () => void;
  onSuccess: (updatedEpisode?: SourceEpisode) => void;
}

const VideoEditorModal: React.FC<VideoEditorModalProps> = ({
  visible,
  episode,
  storyId,
  onClose,
  onSuccess,
}) => {
  // State management
  const [panelsMetadata, setPanelsMetadata] = useState<any[]>([]);
  const [editedPanels, setEditedPanels] = useState<any[]>([]);
  const [debouncedEditedPanels, setDebouncedEditedPanels] = useState<any[]>([]);
  const [selectedPanelId, setSelectedPanelId] = useState<string | null>(null);
  const [selectedMotionIndex, setSelectedMotionIndex] = useState<number | null>(null);
  const [selectedSoundIndex, setSelectedSoundIndex] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<string>('motion');
  const voiceAudioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlayingVoice, setIsPlayingVoice] = useState(false);
  
  // Analysis states
  const [isAnalyzingTimestamps, setIsAnalyzingTimestamps] = useState(false);
  const [isAnalyzingEffects, setIsAnalyzingEffects] = useState(false);
  
  // Video generation
  const [isGeneratingVideo, setIsGeneratingVideo] = useState(false);
  const [videoStatus, setVideoStatus] = useState<string>('not_generated');
  const [renderJobId, setRenderJobId] = useState<string | null>(null);
  const [renderProgress, setRenderProgress] = useState<number>(0);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // Audio editing
  const [editedBgmId, setEditedBgmId] = useState<string | undefined>(episode.backgroundMusicId);
  const [editedBgmVolume, setEditedBgmVolume] = useState<number>(episode.backgroundVolume || 0.25);
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Player controls
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const currentTimeRef = useRef(0); // Use ref to avoid re-renders
  const [mutedTracks, setMutedTracks] = useState({ voice: false, bgm: false, sfx: false });
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [audioDuration, setAudioDuration] = useState<number | null>(null);
  const playerRef = useRef<SimpleVideoPlayerHandle>(null);
  const timeTrackingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const panelSelectDebounceRef = useRef<NodeJS.Timeout | null>(null);
  
  // 🆕 Epic 6: Subtitle states
  const [showSubtitles, setShowSubtitles] = useState(true);
  const [selectedSubtitleLang, setSelectedSubtitleLang] = useState<string>('en');
  const [currentSubtitle, setCurrentSubtitle] = useState<string>('');
  const [rightPanelTab, setRightPanelTab] = useState<'panel' | 'subtitle'>('panel');

  // Fetch data
  // console.log('[VideoEditor] Panels metadata:', {
  //   panelsMetadataCount: panelsMetadata.length,
  //   firstMetadata: panelsMetadata[0],
  // });

  const { data: musicData, refresh: refreshMusic } = useApi(
    useCallback(() => audioLibraryApi.findMany({ status: 'ACTIVE', type: 'music', pageSize: 500 }), []),
    { immediate: visible }
  );
  const musicList = musicData?.data || [];

  const { data: sfxData, refresh: refreshSfx } = useApi(
    useCallback(() => audioLibraryApi.findMany({ status: 'ACTIVE', type: 'sound_effect', pageSize: 500 }), []),
    { immediate: visible }
  );
  const sfxList = sfxData?.data || [];

  const refreshAudioLibrary = useCallback(() => {
    refreshMusic();
    refreshSfx();
  }, [refreshMusic, refreshSfx]);

  // Sync data - ALWAYS reset when modal opens or episode changes
  useEffect(() => {
    if (visible && episode.panels) {
      console.log('[VideoEditor] Syncing data from episode.panels:', episode.panels.map(p => ({
        panelId: p.panelId,
        start: p.startTime,
        end: p.endTime,
        duration: (p.endTime && p.startTime) ? (p.endTime - p.startTime) : 0
      })));
      
      const panelsData = [...episode.panels];
      setPanelsMetadata(panelsData);
      setEditedPanels(panelsData);
      // Set debouncedEditedPanels immediately on sync (no debounce for initial load)
      setDebouncedEditedPanels(panelsData);
      setHasChanges(false);
      
      // Auto-select first panel and set tab to "time"
      if (episode.panels.length > 0) {
        setSelectedPanelId(episode.panels[0].panelId);
        setActiveTab('time');
        setSelectedMotionIndex(null);
        setSelectedSoundIndex(null);
      }
      
      // Load audio duration
      if (episode.masterAudioUrl) {
        const audio = new Audio(episode.masterAudioUrl);
        audio.addEventListener('loadedmetadata', () => {
          console.log('[VideoEditor] Audio duration loaded:', audio.duration);
          setAudioDuration(audio.duration);
        });
        audio.addEventListener('error', (e) => {
          console.error('[VideoEditor] Failed to load audio:', e);
          setAudioDuration(null);
        });
      } else {
        setAudioDuration(null);
      }
    } else if (visible && !episode.panels) {
      // Clear all if no panels
      setPanelsMetadata([]);
      setEditedPanels([]);
      setDebouncedEditedPanels([]);
      setAudioDuration(null);
    }
  }, [visible, episode]);
  
  // Debounce editedPanels updates to prevent video stuttering during edits
  // But skip if this is the initial sync (checked by comparing with panelsMetadata)
  useEffect(() => {
    // Skip debounce if panels are empty (initial state)
    if (editedPanels.length === 0) {
      return;
    }
    
    // Skip debounce if this is initial sync (editedPanels === panelsMetadata)
    if (JSON.stringify(editedPanels) === JSON.stringify(panelsMetadata)) {
      return;
    }
    
    const timer = setTimeout(() => {
      console.log('[VideoEditor] Debouncing editedPanels update...');
      setDebouncedEditedPanels([...editedPanels]);
    }, 300);
    
    return () => clearTimeout(timer);
  }, [editedPanels]);

  useEffect(() => {
    if (visible) {
      setEditedBgmId(episode.backgroundMusicId);
      setEditedBgmVolume(episode.backgroundVolume || 0.25);
      
      // Sync video status
      setVideoStatus(episode.videoStatus || 'not_generated');
      
      // If video is processing, start polling
      if (episode.videoJobId && (episode.videoStatus === 'queued' || episode.videoStatus === 'processing')) {
        setRenderJobId(episode.videoJobId);
        setIsGeneratingVideo(true);
        startPollingJobStatus(episode.videoJobId);
      }
    }
  }, [visible, episode]);

  // Calculate durations (memoized to prevent re-renders)
  const totalDuration = React.useMemo(() => {
    const total = editedPanels.reduce((acc, pm) => acc + (pm.endTime - pm.startTime), 0);
    console.log('[VideoEditor] Total duration:', total, 'from', editedPanels.length, 'panels');
    return total;
  }, [editedPanels]);
  
  const videoEditorPanels: VideoEditorPanel[] = React.useMemo(() => {
    console.log('[VideoEditor] videoEditorPanels RECALCULATING from debouncedEditedPanels:'); 
    debouncedEditedPanels.forEach((p, idx) => {
      console.log(`  Panel ${idx}: ${p.startTime?.toFixed(2)}s - ${p.endTime?.toFixed(2)}s = ${(p.endTime - p.startTime)?.toFixed(2)}s`);
    });
    
    const result = debouncedEditedPanels.map((panel) => {
      if (!panel.imageUrl) {
        console.warn('[VideoEditor] Panel missing imageUrl:', panel.panelId, panel);
      }
      
      return {
        id: panel.panelId,
        imagePath: panel.imageUrl || '',
        caption: panel.caption || '',
        backgroundColor: '#000000',
        startTime: panel.startTime, // Pass actual audio timestamp
        endTime: panel.endTime, // Pass actual audio timestamp
        motionEffects: panel.motionEffects || [],
        soundEffects: panel.soundEffects || [],
        // Epic 7: Video panel fields
        mediaType: panel.mediaType || 'IMAGE',
        videoUrl: panel.videoUrl || undefined,
        videoDuration: panel.videoDuration || undefined,
        firstFrameUrl: panel.firstFrameUrl || undefined,
      };
    });
    
    console.log('[VideoEditor] videoEditorPanels CREATED:', result.length, 'panels');
    return result;
  }, [debouncedEditedPanels]);

  const panelDurations = React.useMemo(() => {
    console.log('[VideoEditor] Calculating panelDurations from debouncedEditedPanels:', debouncedEditedPanels.length, 'panels');
    
    const durations = debouncedEditedPanels.map((pm, idx) => {
      const startTime = typeof pm.startTime === 'number' ? pm.startTime : parseFloat(pm.startTime || '0');
      const endTime = typeof pm.endTime === 'number' ? pm.endTime : parseFloat(pm.endTime || '0');
      
      const duration = (endTime && startTime !== undefined) ? (endTime - startTime) * 1000 : 5000;
      console.log(`  Panel ${idx} duration: ${duration}ms (${startTime}s - ${endTime}s)`);
      
      return duration;
    });
    
    console.log('[VideoEditor] Final panelDurations:', durations, 'Audio duration:', audioDuration);
    return durations;
  }, [debouncedEditedPanels, audioDuration]);

  const backgroundMusicUrl = React.useMemo(() => 
    editedBgmId ? musicList.find((m: AudioAsset) => m.id === editedBgmId)?.url : undefined,
    [editedBgmId, musicList]
  );

  // Progress calculation
  const hasTimestamps = panelsMetadata.length > 0 && panelsMetadata.some(p => 
    p.startTime !== undefined && p.endTime !== undefined && p.endTime > 0
  );
  const hasMotion = panelsMetadata.some(p => p.motionEffects && p.motionEffects.length > 0);
  const hasAudio = editedBgmId || editedPanels.some(p => p.soundEffects && p.soundEffects.length > 0);
  const currentStep = !hasTimestamps ? 0 : (!hasMotion && !hasAudio) ? 1 : 2;


  // Handlers
  const handleAnalyzeTimestamps = async () => {
    setIsAnalyzingTimestamps(true);
    try {
      const result = await sourceEpisodesApi.analyzeTimestamps(storyId, episode.id);
      message.success('✅ Timestamps analyzed! Motion + Audio effects đã được reset.');
      
      // Reload episode data — backend đã clear motionEffects, soundEffects, BGM
      if (result?.data?.panels) {
        setPanelsMetadata([...result.data.panels]);
        setEditedPanels([...result.data.panels]);
      }
      // Clear BGM local state
      setEditedBgmId(undefined);
      
      onSuccess();
    } catch (error: any) {
      message.error('Failed to analyze: ' + error.message);
    } finally {
      setIsAnalyzingTimestamps(false);
    }
  };

  // Analysis handlers
  const handleAnalyzeComplete = async () => {
    setIsAnalyzingEffects(true);
    try {
      const result = await sourceEpisodesApi.analyzeComplete(storyId, episode.id);
      message.success('✅ Motion + Audio analyzed successfully!');
      
      // Reload episode data
      if (result?.data?.panels) {
        setPanelsMetadata([...result.data.panels]);
        setEditedPanels([...result.data.panels]);
      }
      if (result?.data?.backgroundMusicId) {
        setEditedBgmId(result.data.backgroundMusicId);
        setEditedBgmVolume(result.data.backgroundVolume || 0.25);
      }
      
      onSuccess();
    } catch (error: any) {
      message.error('Failed to analyze: ' + error.message);
    } finally {
      setIsAnalyzingEffects(false);
    }
  };

  const handleAnalyzeMotionOnly = async () => {
    setIsAnalyzingEffects(true);
    try {
      const result = await sourceEpisodesApi.analyzeMotion(storyId, episode.id);
      message.success('✅ Motion effects analyzed successfully!');
      
      // Reload episode data
      if (result?.data?.panels) {
        setPanelsMetadata([...result.data.panels]);
        setEditedPanels([...result.data.panels]);
      }
      
      onSuccess();
    } catch (error: any) {
      message.error('Failed to analyze motion: ' + error.message);
    } finally {
      setIsAnalyzingEffects(false);
    }
  };

  const handleAnalyzeAudioOnly = async () => {
    setIsAnalyzingEffects(true);
    try {
      const result = await sourceEpisodesApi.analyzeAudioOnly(storyId, episode.id);
      message.success('✅ Audio (BGM + SFX) analyzed successfully!');
      
      // Reload episode data
      if (result?.data?.panels) {
        setPanelsMetadata([...result.data.panels]);
        setEditedPanels([...result.data.panels]);
      }
      if (result?.data?.backgroundMusicId) {
        setEditedBgmId(result.data.backgroundMusicId);
        setEditedBgmVolume(result.data.backgroundVolume || 0.25);
      }
      
      onSuccess();
    } catch (error: any) {
      message.error('Failed to analyze audio: ' + error.message);
    } finally {
      setIsAnalyzingEffects(false);
    }
  };

  const handleSaveChanges = async () => {
    setSaving(true);
    try {
      // Save BGM (including null/undefined to clear it)
      await sourceEpisodesApi.updateBackgroundMusic(
        episode.id, 
        editedBgmId || null, 
        editedBgmVolume
      );
      
      // Save panels (motion + SFX + subtitleEntries)
      await sourceEpisodesApi.updatePanelsData(episode.id, editedPanels);
      
      // Only regenerate VTT if subtitleEntries actually changed vs original data
      const hasSubtitleChanges = editedPanels.some((panel, index) => {
        const original = panelsMetadata[index];
        return JSON.stringify(panel.subtitleEntries) !== JSON.stringify(original?.subtitleEntries);
      });
      if (hasSubtitleChanges) {
        try {
          // Call backend to regenerate VTT files from updated subtitleEntries
          await sourceEpisodesApi.regenerateSubtitles(episode.id);
          message.success('Changes saved and subtitles regenerated!');
        } catch (error: any) {
          message.warning('Changes saved but subtitle regeneration failed: ' + error.message);
        }
      } else {
        message.success('Changes saved successfully!');
      }
      
      setHasChanges(false);
      onSuccess();
    } catch (error: any) {
      message.error('Failed to save: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateVideo = async () => {
    setIsGeneratingVideo(true);
    setVideoStatus('queued');
    setRenderProgress(0);
    
    try {
      const result = await sourceEpisodesApi.generateVideo(episode.id);
      
      if (result.data.success && result.data.jobId) {
        setRenderJobId(result.data.jobId);
        message.success('Video render job queued! Rendering in background...');
        
        // Start polling job status
        startPollingJobStatus(result.data.jobId);
      } else {
        throw new Error(result.data.error || 'Failed to queue render job');
      }
    } catch (error: any) {
      message.error('Failed to queue video render: ' + error.message);
      setVideoStatus('failed');
      setIsGeneratingVideo(false);
    }
  };

  const startPollingJobStatus = (jobId: string) => {
    // Clear existing interval
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }
    
    // Poll every 2 seconds
    pollIntervalRef.current = setInterval(async () => {
      try {
        const statusResult = await sourceEpisodesApi.getVideoRenderJobStatus(jobId);
        const jobStatus = statusResult.data;
        
        // Map job status to episode status (pending → queued for UI consistency)
        const mappedStatus = jobStatus.status === 'pending' ? 'queued' : jobStatus.status;
        setVideoStatus(mappedStatus);
        setRenderProgress(jobStatus.progress || 0);
        
        // Stop polling when completed or failed
        if (jobStatus.status === 'completed' || jobStatus.status === 'failed') {
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
          
          setIsGeneratingVideo(false);
          
          if (jobStatus.status === 'completed') {
            message.success('Video rendered successfully!');
            onSuccess(); // Reload episode data
          } else {
            message.error(`Video render failed: ${jobStatus.errorMessage || 'Unknown error'}`);
          }
        }
      } catch (error: any) {
        console.error('[VideoEditor] Error polling job status:', error);
      }
    }, 2000);
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  // Player controls
  const handlePlayPause = () => {
    // console.log('[VideoEditor] handlePlayPause called - current isPlaying:', isPlaying);
    setIsPlaying(!isPlaying);
    if (playerRef.current) {
      if (isPlaying) {
        // console.log('[VideoEditor] Pausing player');
        playerRef.current.pause();
      } else {
        // console.log('[VideoEditor] Playing player');
        playerRef.current.play();
      }
    }
  };

  const handlePrevPanel = () => {
    const currentIndex = panelsMetadata.findIndex(p => p.panelId === selectedPanelId);
    if (currentIndex > 0) {
      const prevPanel = panelsMetadata[currentIndex - 1];
      setSelectedPanelId(prevPanel.panelId);
      // Seek to panel start
      if (playerRef.current) {
        const targetFrame = Math.floor(prevPanel.startTime * 60);
        playerRef.current.seekTo(targetFrame);
      }
    }
  };

  const handleNextPanel = () => {
    const currentIndex = panelsMetadata.findIndex(p => p.panelId === selectedPanelId);
    if (currentIndex < panelsMetadata.length - 1) {
      const nextPanel = panelsMetadata[currentIndex + 1];
      setSelectedPanelId(nextPanel.panelId);
      // Seek to panel start
      if (playerRef.current) {
        const targetFrame = Math.floor(nextPanel.startTime * 60);
        playerRef.current.seekTo(targetFrame);
      }
    }
  };

  const handleSeek = (time: number) => {
    // console.log('[VideoEditor] handleSeek:', time.toFixed(2));
    currentTimeRef.current = time;
    setCurrentTime(time);
    const panel = panelsMetadata.find(p => time >= p.startTime && time <= p.endTime);
    if (panel) {
      setSelectedPanelId(panel.panelId);
    }
    // Seek player to frame
    if (playerRef.current) {
      const targetFrame = Math.floor(time * 60); // fps = 60
      playerRef.current.seekTo(targetFrame);
    }
  };

  const handleMotionSelect = (panelId: string, motionIndex: number) => {
    setSelectedPanelId(panelId);
    setSelectedMotionIndex(motionIndex);
    setActiveTab('motion');
    
    // Auto-scroll to motion effect after a short delay
    setTimeout(() => {
      const element = document.getElementById(`motion-effect-${motionIndex}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  const handleSoundSelect = (panelId: string, soundIndex: number) => {
    setSelectedPanelId(panelId);
    setSelectedSoundIndex(soundIndex);
    setActiveTab('sfx');
    
    // Auto-scroll to sound effect after a short delay
    setTimeout(() => {
      const element = document.getElementById(`sound-effect-${soundIndex}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  const handleToggleMute = (track: 'voice' | 'bgm' | 'sfx') => {
    setMutedTracks(prev => ({ ...prev, [track]: !prev[track] }));
  };

  // Audio editing handlers
  const handleBgmChange = (value: string | undefined) => {
    setEditedBgmId(value);
    setHasChanges(true);
  };

  const handleBgmVolumeChange = (value: number | null) => {
    if (value !== null) {
      setEditedBgmVolume(value);
      setHasChanges(true);
    }
  };

  const handleAddSfx = (panelIndex: number) => {
    const newPanels = [...editedPanels];
    if (!newPanels[panelIndex].soundEffects) {
      newPanels[panelIndex].soundEffects = [];
    }
    newPanels[panelIndex].soundEffects.push({
      id: `sfx-${Date.now()}`,
      audioId: sfxList[0]?.id || '',
      audioName: sfxList[0]?.name || '',
      audioUrl: sfxList[0]?.url || '',
      triggerTime: 0,
      duration: 1.5,
      volume: 0.5,
      reasoning: ''
    });
    setEditedPanels(newPanels);
    setHasChanges(true);
  };

  const handleEditSfx = (panelIndex: number, sfxIndex: number, updates: any) => {
    const newPanels = [...editedPanels];
    if (updates.audioId) {
      const selectedSfx = sfxList.find((s: AudioAsset) => s.id === updates.audioId);
      if (selectedSfx) {
        updates.audioName = selectedSfx.name;
        updates.audioUrl = selectedSfx.url;
      }
    }
    newPanels[panelIndex].soundEffects[sfxIndex] = {
      ...newPanels[panelIndex].soundEffects[sfxIndex],
      ...updates
    };
    setEditedPanels(newPanels);
    setHasChanges(true);
  };

  const handleDeleteSfx = (panelIndex: number, sfxIndex: number) => {
    const newPanels = [...editedPanels];
    newPanels[panelIndex].soundEffects.splice(sfxIndex, 1);
    setEditedPanels(newPanels);
    setHasChanges(true);
  };

  /** Kéo thả / resize SFX trên timeline (có thể chuyển sang panel khác) */
  const handleSfxTimelineUpdate = useCallback(
    (u: {
      fromPanelId: string;
      sfxIndex: number;
      sfxId?: string;
      triggerTime: number;
      duration: number;
      targetPanelId: string;
    }) => {
      setEditedPanels((prev) => {
        const panels = prev.map((p) => ({
          ...p,
          soundEffects: [...(p.soundEffects || [])],
        }));
        const fromIdx = panels.findIndex((p) => p.panelId === u.fromPanelId);
        if (fromIdx < 0) return prev;

        let sfxIndex = u.sfxIndex;
        if (u.sfxId) {
          const found = panels[fromIdx].soundEffects.findIndex((s: any) => s.id === u.sfxId);
          if (found >= 0) sfxIndex = found;
        }
        const list = panels[fromIdx].soundEffects;
        if (sfxIndex < 0 || sfxIndex >= list.length) return prev;

        const sfx = { ...list[sfxIndex] };

        if (u.fromPanelId === u.targetPanelId) {
          list[sfxIndex] = {
            ...sfx,
            triggerTime: u.triggerTime,
            duration: u.duration,
          };
          return panels;
        }

        list.splice(sfxIndex, 1);
        const toIdx = panels.findIndex((p) => p.panelId === u.targetPanelId);
        if (toIdx < 0) return prev;
        if (!panels[toIdx].soundEffects) panels[toIdx].soundEffects = [];
        panels[toIdx].soundEffects.push({
          ...sfx,
          triggerTime: u.triggerTime,
          duration: u.duration,
        });
        return panels;
      });
      setHasChanges(true);
    },
    []
  );

  const handleEditMotion = (panelIndex: number, motionIndex: number, updates: any) => {
    const newPanels = [...editedPanels];
    if (!newPanels[panelIndex].motionEffects) {
      newPanels[panelIndex].motionEffects = [];
    }
    newPanels[panelIndex].motionEffects[motionIndex] = {
      ...newPanels[panelIndex].motionEffects[motionIndex],
      ...updates
    };
    setEditedPanels(newPanels);
    setHasChanges(true);
  };

  const handleDeleteMotion = (panelIndex: number, motionIndex: number) => {
    const newPanels = [...editedPanels];
    newPanels[panelIndex].motionEffects.splice(motionIndex, 1);
    setEditedPanels(newPanels);
    setHasChanges(true);
  };

  const handleAddMotion = (panelIndex: number) => {
    const newPanels = [...editedPanels];
    if (!newPanels[panelIndex].motionEffects) {
      newPanels[panelIndex].motionEffects = [];
    }
    const panelDuration = newPanels[panelIndex].endTime - newPanels[panelIndex].startTime;
    newPanels[panelIndex].motionEffects.push({
      id: `motion-${Date.now()}`,
      type: 'zoom-in',
      startOffset: 0,
      duration: Math.min(panelDuration, 3),
      intensity: 1.2,
      focusPoint: { x: 0.5, y: 0.5 },
      overlay: false,
      confidence: 1,
      reasoning: 'Manual effect'
    });
    setEditedPanels(newPanels);
    setHasChanges(true);
  };

  // Keyboard shortcuts
  useEffect(() => {
    if (!visible) return;

    const handleKeyPress = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') return;

      if (e.code === 'Space') {
        e.preventDefault();
        handlePlayPause();
      }
      if (e.code === 'ArrowLeft') {
        e.preventDefault();
        e.shiftKey ? handlePrevPanel() : handleSeek(Math.max(0, currentTime - 1));
      }
      if (e.code === 'ArrowRight') {
        e.preventDefault();
        e.shiftKey ? handleNextPanel() : handleSeek(Math.min(totalDuration, currentTime + 1));
      }
      if ((e.metaKey || e.ctrlKey) && e.code === 'KeyS') {
        e.preventDefault();
        if (hasChanges) handleSaveChanges();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [visible, isPlaying, currentTime, selectedPanelId, hasChanges, totalDuration]);

  // Track player time + update subtitle
  useEffect(() => {
    // console.log('[VideoEditor] useEffect triggered - visible:', visible, 'isPlaying:', isPlaying);
    
    if (!visible) return;

    // Clear existing interval first
    if (timeTrackingIntervalRef.current) {
      console.log('[VideoEditor] Clearing existing interval');
      clearInterval(timeTrackingIntervalRef.current);
      timeTrackingIntervalRef.current = null;
    }

    // Only start interval if playing
    if (isPlaying && playerRef.current) {
      // console.log('[VideoEditor] Starting new interval');
      let lastStateUpdateTime = 0;
      let lastSelectedPanel = selectedPanelId;
      let intervalCount = 0;

      timeTrackingIntervalRef.current = setInterval(() => {
        intervalCount++;
        if (playerRef.current) {
          const currentFrame = playerRef.current.getCurrentFrame();
          const time = currentFrame / 60; // fps = 60
          
          // Always update ref (no re-render)
          currentTimeRef.current = time;
          
          // 🆕 Epic 6: Update current subtitle
          if (showSubtitles && episode.subtitles) {
            const subtitlesData = episode.subtitles as any;
            if (subtitlesData?.files?.[selectedSubtitleLang]) {
              // Find subtitle entry for current time
              let foundSubtitle = '';
              for (const panel of editedPanels) {
                const entries = panel.subtitleEntries?.[selectedSubtitleLang];
                if (entries && Array.isArray(entries)) {
                  for (const entry of entries) {
                    if (time >= entry.startTime && time < entry.endTime) {
                      foundSubtitle = entry.text;
                      break;
                    }
                  }
                }
                if (foundSubtitle) break;
              }
              setCurrentSubtitle(foundSubtitle);
            }
          }
          
          // if (intervalCount % 10 === 0) {
          //   console.log('[VideoEditor] Interval tick', intervalCount, 'time:', time.toFixed(2));
          // }
          
          // Only update STATE every 0.5s (reduce re-renders drastically)
          if (Math.abs(time - lastStateUpdateTime) >= 0.5) {
            // console.log('[VideoEditor] Updating currentTime STATE:', time.toFixed(2));
            setCurrentTime(time);
            lastStateUpdateTime = time;
          }
          
          // Skip auto-select during playback to prevent re-renders
          // User can still manually select panels or use prev/next buttons
        }
      }, 200); // Poll every 200ms
    }

    return () => {
      // console.log('[VideoEditor] Cleanup - clearing interval');
      if (timeTrackingIntervalRef.current) {
        clearInterval(timeTrackingIntervalRef.current);
        timeTrackingIntervalRef.current = null;
      }
    };
  }, [visible, isPlaying, panelsMetadata, showSubtitles, selectedSubtitleLang, editedPanels, episode.subtitles]);

  // Get selected panel data
  const selectedPanelIndex = panelsMetadata.findIndex(p => p.panelId === selectedPanelId);
  const selectedPanel = selectedPanelIndex >= 0 ? panelsMetadata[selectedPanelIndex] : null;
  const selectedEditedPanel = selectedPanelIndex >= 0 ? editedPanels[selectedPanelIndex] : null;

  return (
    <Modal
      title={
        <div>
          <Title level={4} style={{ margin: 0 }}>
            🎬 Video Editor: {episode.name}
          </Title>
          <Steps
            current={currentStep}
            size="small"
            style={{ marginTop: 12 }}
            items={[
              {
                title: 'Timestamps',
                icon: <ClockCircleOutlined />,
                status: hasTimestamps ? 'finish' : currentStep === 0 ? 'process' : 'wait',
              },
              {
                title: 'Effects',
                icon: <ThunderboltOutlined />,
                status: (hasMotion || hasAudio) ? 'finish' : currentStep === 1 ? 'process' : 'wait',
              },
              {
                title: 'Audio',
                icon: <AudioOutlined />,
                status: episode.audioStatus === 'ready' ? 'finish' : 'wait',
              },
              {
                title: 'Export',
                icon: <VideoCameraOutlined />,
                status: videoStatus === 'completed' ? 'finish' : currentStep === 2 ? 'process' : 'wait',
              },
            ]}
          />
        </div>
      }
      open={visible}
      onCancel={onClose}
      width="98%"
      style={{ top: 10, maxWidth: 1800 }}
      styles={{ body: { height: 'calc(100vh - 200px)', overflow: 'hidden', padding: 0 } }}
      footer={[
        <Button key="close" onClick={onClose}>
          ← Back to Episodes
        </Button>,
        <Button
          key="save"
          icon={<SaveOutlined />}
          onClick={handleSaveChanges}
          disabled={!hasChanges || saving}
          loading={saving}
          type="primary"
        >
          {hasChanges && <Tag color="orange" style={{ marginRight: 4 }}>●</Tag>}
          Save Changes
        </Button>,
        <Button
          key="generate"
          icon={<VideoCameraOutlined />}
          onClick={handleGenerateVideo}
          disabled={isGeneratingVideo}
          loading={isGeneratingVideo}
          type="primary"
          size="large"
          style={{ background: '#52c41a', borderColor: '#52c41a' }}
        >
          🎬 Generate Video
        </Button>,
      ]}
    >
      {/* CapCut-style Layout: Horizontal 3-column */}
      <div style={{ display: 'flex', height: '100%', gap: '0', backgroundColor: '#1a1a1a' }}>
        
        {/* LEFT SIDEBAR: Panels Library (12%) */}
        <div style={{ 
          width: '12%', 
          minWidth: '180px',
          backgroundColor: '#2a2a2a', 
          borderRight: '1px solid #3a3a3a',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          <div style={{ padding: '12px', borderBottom: '1px solid #3a3a3a' }}>
            <Text strong style={{ color: '#fff', fontSize: '13px' }}>📚 Panels</Text>
            <Tag color="blue" style={{ marginLeft: 8, fontSize: '10px' }}>{panelsMetadata.length}</Tag>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: '8px' }}>
            {panelsMetadata.length > 0 ? (
              <Space direction="vertical" style={{ width: '100%' }} size="small">
                {panelsMetadata.map((pm, idx) => {
                  const isSelected = pm.panelId === selectedPanelId;
                  const isPlaying = currentTime >= pm.startTime && currentTime <= pm.endTime;
                  const hasMotion = editedPanels[idx]?.motionEffects?.length > 0;
                  const hasSfx = editedPanels[idx]?.soundEffects?.length > 0;

                  return (
                    <Card
                      key={pm.panelId}
                      size="small"
                      hoverable
                      onClick={() => setSelectedPanelId(pm.panelId)}
                      style={{
                        cursor: 'pointer',
                        borderLeft: isPlaying ? '3px solid #52c41a' : (isSelected ? '2px solid #1890ff' : '1px solid #3a3a3a'),
                        borderRight: isSelected ? '2px solid #1890ff' : '1px solid #3a3a3a',
                        borderTop: isSelected ? '2px solid #1890ff' : '1px solid #3a3a3a',
                        borderBottom: isSelected ? '2px solid #1890ff' : '1px solid #3a3a3a',
                        backgroundColor: isPlaying ? '#1e3a5f' : '#333',
                      }}
                      styles={{ body: { padding: '6px' } }}
                    >
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        {pm.imageUrl && (
                          <img
                            src={pm.imageUrl}
                            alt={`Panel ${idx + 1}`}
                            style={{ width: 40, height: 60, objectFit: 'cover', borderRadius: 4 }}
                          />
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <Text strong style={{ color: '#fff', fontSize: '11px', display: 'block' }}>
                            #{idx + 1}
                          </Text>
                          <Text style={{ color: '#999', fontSize: '10px', display: 'block' }}>
                            {(pm.endTime - pm.startTime).toFixed(1)}s
                          </Text>
                          <Space size={2}>
                            {hasMotion && <Tag color="purple" style={{ fontSize: '8px', margin: 0, padding: '0 3px' }}>M</Tag>}
                            {hasSfx && <Tag color="orange" style={{ fontSize: '8px', margin: 0, padding: '0 3px' }}>S</Tag>}
                          </Space>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </Space>
            ) : (
              <Empty 
                description="No panels" 
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                style={{ marginTop: 40 }}
              />
            )}
          </div>
        </div>

        {/* CENTER: Video Preview + Timeline (66%) */}
        <div style={{ 
          flex: 1,
          display: 'flex', 
          flexDirection: 'column',
          backgroundColor: '#1a1a1a',
          overflow: 'hidden'
        }}>
          {/* Video Preview */}
          <div style={{ 
            flex: 1, 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            padding: '24px',
            minHeight: 0,
            position: 'relative'
          }}>
            <IsolatedVideoPlayer
              ref={playerRef}
              panels={videoEditorPanels}
              panelDurations={panelDurations}
              audioPath={episode.masterAudioUrl}
              backgroundMusicUrl={backgroundMusicUrl}
              backgroundVolume={editedBgmVolume}
              aspectRatio="9:16"
              mutedTracks={mutedTracks}
              playbackSpeed={playbackSpeed}
              audioDuration={audioDuration}
              hasTimestamps={hasTimestamps}
            />
            
            {/* 🆕 Epic 6: Subtitle Overlay */}
            {showSubtitles && currentSubtitle && (
              <div style={{
                position: 'absolute',
                bottom: '12%',
                left: '50%',
                transform: 'translateX(-50%)',
                color: '#FFFFFF',
                padding: '4px 12px',
                fontSize: '16px',
                fontFamily: 'Arial, sans-serif',
                textAlign: 'center',
                maxWidth: '85%',
                zIndex: 10,
                pointerEvents: 'none',
                lineHeight: '1.6',
                textShadow: '2px 2px 4px rgba(0, 0, 0, 0.9), -1px -1px 2px rgba(0, 0, 0, 0.9), 1px -1px 2px rgba(0, 0, 0, 0.9), -1px 1px 2px rgba(0, 0, 0, 0.9)',
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word',
                fontWeight: '600'
              }}>
                {currentSubtitle}
              </div>
            )}
          </div>

          {/* Player Controls */}
          <div style={{ backgroundColor: '#1e1e1e', borderTop: '1px solid #3a3a3a' }}>
            <EnhancedPlayerControls
              isPlaying={isPlaying}
              onPlayPause={handlePlayPause}
              onPrevPanel={handlePrevPanel}
              onNextPanel={handleNextPanel}
              currentTime={currentTime}
              totalDuration={totalDuration}
              onSeek={handleSeek}
              mutedTracks={mutedTracks}
              onToggleMute={handleToggleMute}
              playbackSpeed={playbackSpeed}
              onSpeedChange={setPlaybackSpeed}
              subtitle={
                episode.subtitles && (episode.subtitles as any).files && Object.keys((episode.subtitles as any).files).length > 0
                  ? {
                      show: showSubtitles,
                      onToggle: setShowSubtitles,
                      lang: selectedSubtitleLang,
                      onLangChange: setSelectedSubtitleLang,
                      languages: Object.keys((episode.subtitles as any).files).map(lang => {
                        const file = (episode.subtitles as any).files[lang];
                        return { value: lang, label: `${file.languageName || lang} (${file.entriesCount || 0})` };
                      }),
                    }
                  : undefined
              }
            />
          </div>

          {/* Timeline */}
          {hasTimestamps && (
            <div style={{ 
              height: '300px',
              backgroundColor: '#2a2a2a',
              borderTop: '1px solid #3a3a3a',
              display: 'flex',
              flexDirection: 'column',
            }}>
              <ProfessionalTimeline
                panels={editedPanels.map((pm) => ({
                  panelId: pm.panelId,
                  orderIndex: pm.orderIndex,
                  startTime: pm.startTime,
                  endTime: pm.endTime,
                  imageUrl: pm.imageUrl,
                  caption: pm.caption,
                  motionEffects: pm.motionEffects || [],
                  soundEffects: pm.soundEffects || [],
                }))}
                totalDuration={totalDuration}
                currentTime={currentTime}
                selectedPanelId={selectedPanelId}
                onPanelSelect={setSelectedPanelId}
                onSeek={handleSeek}
                backgroundMusicUrl={backgroundMusicUrl}
                onMotionSelect={handleMotionSelect}
                onSoundSelect={handleSoundSelect}
                onSfxTimelineUpdate={handleSfxTimelineUpdate}
              />
            </div>
          )}
        </div>

        {/* RIGHT SIDEBAR: Properties Panel (22%) */}
        <div style={{ 
          width: '22%',
          minWidth: '300px',
          backgroundColor: '#2a2a2a',
          borderLeft: '1px solid #3a3a3a',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          {/* Scrollable container for all right sidebar content */}
          <div style={{ 
            flex: 1,
            overflowY: 'auto',
            padding: '12px'
          }}>
            <Collapse 
              defaultActiveKey={['ai']} 
              style={{ 
                backgroundColor: '#333',
                border: '1px solid #3a3a3a'
              }}
            >
              <CollapsePanel
                header={<Text style={{ color: '#fff' }}>🤖 AI Analysis</Text>}
                key="ai"
                style={{ backgroundColor: '#333', borderColor: '#3a3a3a' }}
              >
                <Space direction="vertical" style={{ width: '100%' }} size="small">
                {/* Step 1: Timestamps */}
                {!hasTimestamps && (
                  <Alert
                    message="Step 1: Analyze Timestamps"
                    description="Split audio into panels first"
                    type="info"
                    showIcon
                    style={{ fontSize: '11px' }}
                  />
                )}
                
                <Button
                  type="primary"
                  icon={<ClockCircleOutlined />}
                  onClick={handleAnalyzeTimestamps}
                  loading={isAnalyzingTimestamps}
                  block
                  size="middle"
                >
                  {hasTimestamps ? '🔄 Re-analyze Timestamps' : '1️⃣ Step 1: Analyze Timestamps'}
                </Button>


                {/* Step 2: Motion & Audio (sau khi có timestamps) */}
                {hasTimestamps && (
                  <>
                    <div style={{ 
                      borderTop: '1px solid #444', 
                      margin: '12px 0 8px 0',
                      paddingTop: '12px'
                    }}>
                      <Text style={{ color: '#999', fontSize: '11px', display: 'block', marginBottom: 8 }}>
                        📝 Step 2: Generate Effects & Audio
                      </Text>
                    </div>

                    {/* Option A: Motion + Audio (Complete) */}
                    <Button
                      type="primary"
                      icon={<ThunderboltOutlined />}
                      onClick={() => handleAnalyzeComplete()}
                      loading={isAnalyzingEffects}
                      block
                      size="middle"
                      style={{ 
                        background: hasMotion && hasAudio ? '#52c41a' : '#722ed1', 
                        borderColor: hasMotion && hasAudio ? '#52c41a' : '#722ed1' 
                      }}
                    >
                      {hasMotion && hasAudio ? '🔄 Re-analyze Motion + Audio' : 'Motion + Audio'}
                    </Button>

                    {/*/!* Option B: Motion Only *!/*/}
                    {/*<Button*/}
                    {/*  icon={<ThunderboltOutlined />}*/}
                    {/*  onClick={() => handleAnalyzeMotionOnly()}*/}
                    {/*  loading={isAnalyzingEffects}*/}
                    {/*  block*/}
                    {/*  size="middle"*/}
                    {/*>*/}
                    {/*  {hasMotion ? '🔄 Re-analyze Motion' : 'Motion Only'}*/}
                    {/*</Button>*/}

                    {/*/!* Option C: Audio Only *!/*/}
                    {/*<Button*/}
                    {/*  icon={<SoundOutlined />}*/}
                    {/*  onClick={() => handleAnalyzeAudioOnly()}*/}
                    {/*  loading={isAnalyzingEffects}*/}
                    {/*  block*/}
                    {/*  size="middle"*/}
                    {/*>*/}
                    {/*  {hasAudio ? '🔄 Re-analyze Audio' : 'Audio Only (BGM + SFX)'}*/}
                    {/*</Button>*/}
                  </>
                )}
              </Space>
            </CollapsePanel>
          </Collapse>

          {/* Background Music */}
          <Collapse 
            defaultActiveKey={['bgm']} 
            style={{ 
              marginTop: 12,
              backgroundColor: '#333',
              border: '1px solid #3a3a3a'
            }}
          >
            <CollapsePanel
              header={
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Text style={{ color: '#fff' }}>🎵 Background Music</Text>
                  <Button
                    type="text"
                    size="small"
                    icon={<ReloadOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      refreshAudioLibrary();
                    }}
                    style={{ color: '#1890ff' }}
                  />
                </div>
              }
              key="bgm"
              style={{ backgroundColor: '#333', borderColor: '#3a3a3a' }}
            >
              <Space direction="vertical" style={{ width: '100%' }} size="small">
                <Select
                  value={editedBgmId}
                  onChange={handleBgmChange}
                  placeholder="Select background music"
                  showSearch
                  style={{ width: '100%' }}
                  size="small"
                  allowClear
                  filterOption={(input, option) =>
                    String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  options={musicList.map((music: AudioAsset) => ({
                    value: music.id,
                    label: `${music.name} ${music.mood?.join(', ') ? `(${music.mood.join(', ')})` : ''}`
                  }))}
                />

                {editedBgmId && (
                  <div>
                    <Text style={{ fontSize: '11px', display: 'block', marginBottom: 4, color: '#999' }}>
                      Volume:
                    </Text>
                    <Slider
                      min={0}
                      max={1}
                      step={0.05}
                      value={editedBgmVolume}
                      onChange={handleBgmVolumeChange}
                      marks={{
                        0: { label: <span style={{ color: '#999' }}>0</span> },
                        0.25: { label: <span style={{ color: '#999' }}>0.25</span> },
                        0.5: { label: <span style={{ color: '#999' }}>0.5</span> },
                        1: { label: <span style={{ color: '#999' }}>1</span> }
                      }}
                    />
                  </div>
                )}
              </Space>
            </CollapsePanel>
          </Collapse>

          {/* Rendered Video Preview */}
          {(episode.videoUrl || (isGeneratingVideo && videoStatus !== 'not_generated')) && (
            <Collapse 
              defaultActiveKey={['video']} 
              style={{ 
                marginTop: 12,
                backgroundColor: '#333',
                border: '1px solid #3a3a3a'
              }}
            >
              <CollapsePanel
                header={<Text style={{ color: '#fff' }}>🎬 Rendered Video</Text>}
                key="video"
                style={{ backgroundColor: '#333', borderColor: '#3a3a3a' }}
              >
                <Space direction="vertical" style={{ width: '100%' }} size="small">
                  {/* Render Progress */}
                  {isGeneratingVideo && videoStatus !== 'completed' && (
                    <div>
                      <Text style={{ fontSize: '11px', color: '#999', display: 'block', marginBottom: 4 }}>
                        Status: {videoStatus === 'queued' ? '⏳ Queued' : 
                                videoStatus === 'processing' ? '🎬 Rendering' : 
                                videoStatus === 'failed' ? '❌ Failed' : videoStatus}
                      </Text>
                      <div style={{ 
                        width: '100%', 
                        height: '4px', 
                        backgroundColor: '#444', 
                        borderRadius: '2px',
                        overflow: 'hidden'
                      }}>
                        <div style={{ 
                          width: `${renderProgress}%`, 
                          height: '100%', 
                          backgroundColor: '#52c41a',
                          transition: 'width 0.3s ease'
                        }} />
                      </div>
                      <Text style={{ fontSize: '10px', color: '#999', marginTop: 4 }}>
                        {renderProgress}%
                      </Text>
                    </div>
                  )}

                  {/* Video Player - Removed as requested to show buttons below */}
                  {/* {episode.videoUrl && (
                    <div style={{ 
                      width: '100%', 
                      backgroundColor: '#000',
                      borderRadius: '4px',
                      overflow: 'hidden'
                    }}>
                      <video 
                        src={episode.videoUrl} 
                        controls 
                        style={{ width: '100%', display: 'block' }}
                      >
                        {episode.subtitles && (episode.subtitles as any).files && Object.entries((episode.subtitles as any).files).map(([lang, file]: [string, any]) => (
                          <track
                            key={lang}
                            kind="subtitles"
                            src={file.url}
                            srcLang={lang}
                            label={file.languageName || lang}
                            default={lang === 'en'}
                          />
                        ))}
                      </video>
                      <div style={{ padding: '8px', backgroundColor: '#1a1a1a' }}>
                        <Text style={{ fontSize: '10px', color: '#52c41a' }}>
                          ✓ Video ready
                        </Text>
                        <br />
                        <a 
                          href={episode.videoUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{ fontSize: '10px', color: '#1890ff' }}
                        >
                          Open in new tab →
                        </a>
                        
                        {episode.subtitles && (episode.subtitles as any).files && Object.keys((episode.subtitles as any).files).length > 0 && (
                          <>
                            <br />
                            <Text style={{ fontSize: '10px', color: '#999', marginTop: '4px', display: 'block' }}>
                              📝 Subtitles:
                            </Text>
                            {Object.entries((episode.subtitles as any).files).map(([lang, file]: [string, any]) => (
                              <div key={lang} style={{ marginTop: '4px' }}>
                                <a 
                                  href={file.url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  style={{ fontSize: '10px', color: '#1890ff', marginRight: '8px' }}
                                >
                                  {file.languageName} (VTT) →
                                </a>
                                <a 
                                  href={`/api/v1/admin/source-episodes/${episode.id}/subtitles/${lang}/download?format=srt`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ fontSize: '10px', color: '#faad14' }}
                                >
                                  (SRT) →
                                </a>
                              </div>
                            ))}
                          </>
                        )}
                      </div>
                    </div>
                  )} */}

                  {/* Show only links/buttons if video exists */}
                  {episode.videoUrl && (
                    <div style={{ padding: '8px', backgroundColor: '#1a1a1a', borderRadius: '4px', border: '1px solid #444' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <Text style={{ fontSize: '11px', color: '#52c41a' }}>
                          ✓ Video ready
                        </Text>
                        <a 
                          href={episode.videoUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{ fontSize: '11px', color: '#1890ff', fontWeight: 'bold' }}
                        >
                          🎬 Open Video →
                        </a>
                      </div>
                      
                      {episode.subtitles && (episode.subtitles as any).files && Object.keys((episode.subtitles as any).files).length > 0 && (
                        <div style={{ borderTop: '1px solid #333', paddingTop: '8px' }}>
                          <Text style={{ fontSize: '11px', color: '#999', display: 'block', marginBottom: 4 }}>
                            📝 Subtitles:
                          </Text>
                          {Object.entries((episode.subtitles as any).files).map(([lang, file]: [string, any]) => (
                            <div key={lang} style={{ display: 'flex', gap: '12px', marginBottom: 4 }}>
                              <a 
                                href={file.url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                style={{ fontSize: '10px', color: '#1890ff' }}
                              >
                                {file.languageName} (VTT)
                              </a>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {videoStatus === 'failed' && (
                    <Alert
                      message="Render Failed"
                      description="Video rendering failed. Please try again."
                      type="error"
                      showIcon
                      style={{ fontSize: '11px' }}
                    />
                  )}
                </Space>
              </CollapsePanel>
            </Collapse>
          )}

          {/* 🆕 Epic 6: Top-level tabs for Panel Editor vs Subtitle Editor */}
          <Tabs
            activeKey={rightPanelTab}
            onChange={(key) => setRightPanelTab(key as 'panel' | 'subtitle')}
            style={{ 
              padding: '0',
              backgroundColor: '#2a2a2a',
              marginTop: 12
            }}
              items={[
                {
                  key: 'panel',
                  label: '🎬 Panel Editor',
                },
                {
                  key: 'subtitle',
                  label: '📝 Subtitles',
                  disabled: !episode.subtitles || !Object.keys((episode.subtitles as any)?.files || {}).length,
                },
              ]}
            />

            {rightPanelTab === 'panel' && (
                selectedPanel ? (
                  <Card
                    title={<Text style={{ color: '#fff' }}>Panel {selectedPanelIndex + 1} Properties</Text>}
                    size="small"
                    style={{ backgroundColor: '#333', border: '1px solid #3a3a3a' }}
                    styles={{ 
                      header: { backgroundColor: '#3a3a3a', borderBottom: '1px solid #444' },
                      body: { backgroundColor: '#333' }
                    }}
                    extra={
                      <Tag color="blue">
                        {(selectedPanel.endTime - selectedPanel.startTime).toFixed(1)}s
                      </Tag>
                    }
                  >
                    <Tabs 
                      activeKey={activeTab} 
                      onChange={setActiveTab}
                      size="small" 
                      style={{ color: '#fff' }}
                    >
                      {/* Timestamps Tab */}
                      <TabPane tab="⏱️ Time" key="time">
                        <Space direction="vertical" style={{ width: '100%' }} size="small">
                          {/* Listen to Voice */}
                          {episode.masterAudioUrl && (
                            <Button
                              icon={isPlayingVoice ? <LoadingOutlined /> : <CustomerServiceOutlined />}
                              onClick={() => {
                                if (isPlayingVoice) {
                                  // Stop
                                  if (voiceAudioRef.current) {
                                    voiceAudioRef.current.pause();
                                    voiceAudioRef.current = null;
                                  }
                                  setIsPlayingVoice(false);
                                } else {
                                  // Play from start
                                  if (voiceAudioRef.current) {
                                    voiceAudioRef.current.pause();
                                  }

                                  const audio = new Audio(episode.masterAudioUrl);
                                  voiceAudioRef.current = audio;
                                  audio.currentTime = selectedPanel.startTime;
                                  setIsPlayingVoice(true);
                                  audio.play();

                                  // Use timeupdate event to check and stop at endTime
                                  const checkTime = () => {
                                    if (audio.currentTime >= selectedPanel.endTime) {
                                      audio.pause();
                                      audio.removeEventListener('timeupdate', checkTime);
                                      setIsPlayingVoice(false);
                                    }
                                  };
                                  audio.addEventListener('timeupdate', checkTime);

                                  // Cleanup on audio end or error
                                  audio.addEventListener('ended', () => {
                                    audio.removeEventListener('timeupdate', checkTime);
                                    setIsPlayingVoice(false);
                                  });
                                  
                                  audio.addEventListener('error', () => {
                                    audio.removeEventListener('timeupdate', checkTime);
                                    setIsPlayingVoice(false);
                                  });
                                }
                              }}
                              block
                              size="small"
                              type={isPlayingVoice ? 'primary' : 'default'}
                            >
                              {isPlayingVoice ? '⏸ Stop' : '🎧 Listen Voice'} ({(selectedPanel.endTime - selectedPanel.startTime).toFixed(1)}s)
                            </Button>
                          )}

                          {selectedPanel.caption && (
                            <div style={{ 
                              padding: '8px',
                              backgroundColor: '#2a2a2a',
                              borderRadius: '4px',
                              border: '1px solid #444'
                            }}>
                              <Text style={{ fontSize: '10px', color: '#999', display: 'block', marginBottom: 4 }}>
                                📝 Caption:
                              </Text>
                              <Text style={{ fontSize: '11px', color: '#fff' }}>
                                {selectedPanel.caption}
                              </Text>
                            </div>
                          )}

                          <div style={{ display: 'flex', gap: '8px' }}>
                            <div style={{ flex: 1 }}>
                              <Text style={{ fontSize: '11px', color: '#999', display: 'block', marginBottom: 4 }}>
                                Start (s)
                              </Text>
                              <InputNumber
                                value={selectedPanel.startTime}
                                onChange={(value) => {
                                  if (value !== null && value >= 0) {
                                    const newPanels = [...editedPanels];
                                    newPanels[selectedPanelIndex] = {
                                      ...newPanels[selectedPanelIndex],
                                      startTime: value
                                    };
                                    setEditedPanels(newPanels);
                                    
                                    // Also update panelsMetadata
                                    const newMetadata = [...panelsMetadata];
                                    newMetadata[selectedPanelIndex] = {
                                      ...newMetadata[selectedPanelIndex],
                                      startTime: value
                                    };
                                    setPanelsMetadata(newMetadata);
                                    
                                    setHasChanges(true);
                                  }
                                }}
                                min={0}
                                max={selectedPanel.endTime - 0.1}
                                step={0.1}
                                size="small"
                                style={{ width: '100%' }}
                              />
                            </div>

                            <div style={{ flex: 1 }}>
                              <Text style={{ fontSize: '11px', color: '#999', display: 'block', marginBottom: 4 }}>
                                End (s)
                              </Text>
                              <InputNumber
                                value={selectedPanel.endTime}
                                onChange={(value) => {
                                  if (value !== null && value > selectedPanel.startTime) {
                                    const newPanels = [...editedPanels];
                                    newPanels[selectedPanelIndex] = {
                                      ...newPanels[selectedPanelIndex],
                                      endTime: value
                                    };
                                    setEditedPanels(newPanels);
                                    
                                    // Also update panelsMetadata
                                    const newMetadata = [...panelsMetadata];
                                    newMetadata[selectedPanelIndex] = {
                                      ...newMetadata[selectedPanelIndex],
                                      endTime: value
                                    };
                                    setPanelsMetadata(newMetadata);
                                    
                                    setHasChanges(true);
                                  }
                                }}
                                min={selectedPanel.startTime + 0.1}
                                step={0.1}
                                size="small"
                                style={{ width: '100%' }}
                              />
                            </div>
                          </div>

                          <div>
                            <Text style={{ fontSize: '11px', color: '#999', display: 'block', marginBottom: 4 }}>
                              Duration: {(selectedPanel.endTime - selectedPanel.startTime).toFixed(2)}s
                            </Text>
                          </div>
                        </Space>
                      </TabPane>

                      {/* Motion Tab */}
                      <TabPane tab="🎬 Motion" key="motion">
                        <Space direction="vertical" style={{ width: '100%' }} size="small">
                          <Button
                            icon={<PlusOutlined />}
                            onClick={() => handleAddMotion(selectedPanelIndex)}
                            block
                            size="small"
                          >
                            Add Motion Effect
                          </Button>

                          {selectedPanel.motionEffects && selectedPanel.motionEffects.length > 0 ? (
                            selectedPanel.motionEffects.map((effect: any, idx: number) => (
                              <Card 
                                key={effect.id} 
                                size="small"
                                id={`motion-effect-${idx}`}
                                style={{ 
                                  fontSize: '12px',
                                  backgroundColor: selectedMotionIndex === idx ? '#4a4a6a' : '#3a3a3a',
                                  border: selectedMotionIndex === idx ? '2px solid #722ed1' : '1px solid #444',
                                  transition: 'all 0.2s',
                                }}
                                styles={{ body: { backgroundColor: selectedMotionIndex === idx ? '#4a4a6a' : '#3a3a3a' } }}
                                onClick={() => setSelectedMotionIndex(idx)}
                              >
                                <Space direction="vertical" size={8} style={{ width: '100%' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Select
                                      value={effect.type}
                                      onChange={(value) => handleEditMotion(selectedPanelIndex, idx, { type: value })}
                                      size="small"
                                      style={{ flex: 1, marginRight: 8 }}
                                      options={[
                                        { label: '🔍 Zoom In', value: 'zoom-in' },
                                        { label: '🔎 Zoom Out', value: 'zoom-out' },
                                        { label: '⬅️ Pan Left', value: 'pan-left' },
                                        { label: '➡️ Pan Right', value: 'pan-right' },
                                        { label: '⬆️ Pan Up', value: 'pan-up' },
                                        { label: '⬇️ Pan Down', value: 'pan-down' },
                                        { label: '🎬 Ken Burns', value: 'ken-burns' },
                                        { label: '🔄 Zoom In-Out', value: 'zoom-in-out' },
                                        { label: '🎯 Double Zoom', value: 'double-zoom' },
                                        { label: '🌊 Slow Pan', value: 'slow-pan' },
                                        { label: '📱 Static', value: 'static' },
                                        { label: '💨 Slide', value: 'slide' },
                                        { label: '✨ Fade', value: 'fade' },
                                        { label: '🔔 Shake', value: 'shake' },
                                        { label: '🔄 Rotate', value: 'rotate' },
                                      ]}
                                    />
                                    <Popconfirm
                                      title="Delete this motion effect?"
                                      onConfirm={() => handleDeleteMotion(selectedPanelIndex, idx)}
                                      okText="Yes"
                                      cancelText="No"
                                    >
                                      <Button
                                        type="text"
                                        danger
                                        size="small"
                                        icon={<DeleteOutlined />}
                                      />
                                    </Popconfirm>
                                  </div>
                                  
                                  <div>
                                    <Text style={{ fontSize: '11px', color: '#999' }}>Start Offset (s)</Text>
                                    <Slider
                                      min={0}
                                      max={selectedPanel.endTime - selectedPanel.startTime}
                                      step={0.1}
                                      value={effect.startOffset}
                                      onChange={(value) => handleEditMotion(selectedPanelIndex, idx, { startOffset: value })}
                                      tooltip={{ formatter: (v) => `${v}s` }}
                                    />
                                  </div>
                                  
                                  <div>
                                    <Text style={{ fontSize: '11px', color: '#999' }}>Duration (s)</Text>
                                    <Slider
                                      min={0.5}
                                      max={5}
                                      step={0.1}
                                      value={effect.duration}
                                      onChange={(value) => handleEditMotion(selectedPanelIndex, idx, { duration: value })}
                                      tooltip={{ formatter: (v) => `${v}s` }}
                                    />
                                  </div>
                                  
                                  <div style={{ display: 'flex', gap: '8px' }}>
                                    <div style={{ flex: 1 }}>
                                      <Text style={{ fontSize: '11px', color: '#999' }}>Intensity</Text>
                                      <Slider
                                        min={0.1}
                                        max={2}
                                        step={0.1}
                                        value={effect.intensity}
                                        onChange={(value) => handleEditMotion(selectedPanelIndex, idx, { intensity: value })}
                                        tooltip={{ formatter: (v) => `${v}x` }}
                                      />
                                    </div>
                                    
                                    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                      <Text style={{ fontSize: '11px', color: '#999', marginBottom: '4px' }}>Overlay</Text>
                                      <Switch
                                        checked={effect.overlay || false}
                                        onChange={(checked) => handleEditMotion(selectedPanelIndex, idx, { overlay: checked })}
                                        size="small"
                                      />
                                    </div>
                                  </div>
                                  
                                  {selectedPanel.imageUrl && (
                                    <>
                                      <FocusPointPicker
                                        imageUrl={selectedPanel.imageUrl}
                                        focusPoint={effect.focusPoint || { x: 0.5, y: 0.5 }}
                                        onChange={(point) => {
                                          if (effect.type === 'double-zoom') {
                                            handleEditMotion(selectedPanelIndex, idx, { 
                                              focusPoint: point,
                                              metadata: {
                                                ...effect.metadata,
                                                firstFocus: point
                                              }
                                            });
                                          } else {
                                            handleEditMotion(selectedPanelIndex, idx, { focusPoint: point });
                                          }
                                        }}
                                      />
                                      
                                      {effect.type === 'double-zoom' && (
                                        <div style={{ marginTop: '8px' }}>
                                          <Text style={{ fontSize: '11px', color: '#999' }}>
                                            Second Focus Point (click on image)
                                          </Text>
                                          <FocusPointPicker
                                            imageUrl={selectedPanel.imageUrl}
                                            focusPoint={effect.metadata?.secondFocus || { x: 0.5, y: 0.5 }}
                                            onChange={(point) => handleEditMotion(selectedPanelIndex, idx, { 
                                              metadata: {
                                                ...effect.metadata,
                                                secondFocus: point
                                              }
                                            })}
                                          />
                                        </div>
                                      )}
                                    </>
                                  )}
                                  
                                  {effect.reasoning && (
                                    <Text style={{ fontSize: '10px', color: '#aaa' }}>
                                      💡 {effect.reasoning}
                                    </Text>
                                  )}
                                </Space>
                              </Card>
                            ))
                          ) : (
                            <Empty 
                              description={<Text style={{ color: '#999' }}>No motion effects</Text>}
                              image={Empty.PRESENTED_IMAGE_SIMPLE} 
                            />
                          )}
                        </Space>
                      </TabPane>

                      {/* Audio Tab */}
                      <TabPane tab="🔊 SFX" key="sfx">
                        <Space direction="vertical" style={{ width: '100%' }} size="small">
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <Button
                              icon={<PlusOutlined />}
                              onClick={() => handleAddSfx(selectedPanelIndex)}
                              disabled={sfxList.length === 0}
                              block
                              size="small"
                              style={{ flex: 1 }}
                            >
                              Add Sound Effect
                            </Button>
                            <Button
                              type="default"
                              size="small"
                              icon={<ReloadOutlined />}
                              onClick={() => refreshAudioLibrary()}
                              style={{ color: '#1890ff' }}
                            />
                          </div>

                          {selectedEditedPanel?.soundEffects && selectedEditedPanel.soundEffects.length > 0 ? (
                            selectedEditedPanel.soundEffects.map((sfx: any, sfxIdx: number) => (
                              <Card 
                                key={sfxIdx} 
                                size="small"
                                id={`sound-effect-${sfxIdx}`}
                                style={{ 
                                  fontSize: '12px',
                                  backgroundColor: selectedSoundIndex === sfxIdx ? '#6a5a4a' : '#3a3a3a',
                                  border: selectedSoundIndex === sfxIdx ? '2px solid #faad14' : '1px solid #444',
                                  transition: 'all 0.2s',
                                }}
                                styles={{ body: { backgroundColor: selectedSoundIndex === sfxIdx ? '#6a5a4a' : '#3a3a3a' } }}
                                onClick={() => setSelectedSoundIndex(sfxIdx)}
                              >
                                <Space direction="vertical" size={8} style={{ width: '100%' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Text strong style={{ fontSize: '12px', color: '#fff' }}>SFX #{sfxIdx + 1}</Text>
                                    <Popconfirm
                                      title="Delete this SFX?"
                                      onConfirm={() => handleDeleteSfx(selectedPanelIndex, sfxIdx)}
                                      okText="Yes"
                                      cancelText="No"
                                    >
                                      <Button
                                        type="text"
                                        danger
                                        size="small"
                                        icon={<DeleteOutlined />}
                                      />
                                    </Popconfirm>
                                  </div>

                                  <Select
                                    value={sfx.audioId}
                                    onChange={(value) => handleEditSfx(selectedPanelIndex, sfxIdx, { audioId: value })}
                                    showSearch
                                    style={{ width: '100%' }}
                                    size="small"
                                    filterOption={(input, option) =>
                                      String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                    }
                                    options={sfxList.map((s: AudioAsset) => ({
                                      value: s.id,
                                      label: s.name
                                    }))}
                                  />

                                  <div style={{ display: 'flex', gap: '8px' }}>
                                    <div style={{ flex: 1 }}>
                                      <Text style={{ fontSize: '10px', display: 'block', color: '#999' }}>
                                        Time (s):
                                      </Text>
                                      <InputNumber
                                        size="small"
                                        style={{ width: '100%' }}
                                        min={0}
                                        max={selectedPanel.endTime - selectedPanel.startTime || 10}
                                        step={0.1}
                                        value={sfx.triggerTime}
                                        onChange={(value) => handleEditSfx(selectedPanelIndex, sfxIdx, { triggerTime: value })}
                                      />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                      <Text style={{ fontSize: '10px', display: 'block', color: '#999' }}>
                                        Dur (s):
                                      </Text>
                                      <InputNumber
                                        size="small"
                                        style={{ width: '100%' }}
                                        min={0.1}
                                        max={10}
                                        step={0.1}
                                        value={sfx.duration || 1.5}
                                        onChange={(value) => handleEditSfx(selectedPanelIndex, sfxIdx, { duration: value })}
                                      />
                                    </div>
                                  </div>
                                  
                                  <div>
                                    <Text style={{ fontSize: '11px', color: '#999', display: 'block', marginBottom: 4 }}>
                                      Volume:
                                    </Text>
                                    <Slider
                                      min={0}
                                      max={1}
                                      step={0.05}
                                      value={sfx.volume}
                                      onChange={(value) => handleEditSfx(selectedPanelIndex, sfxIdx, { volume: value })}
                                      marks={{
                                        0: { label: <span style={{ color: '#999' }}>0</span> },
                                        0.25: { label: <span style={{ color: '#999' }}>0.25</span> },
                                        0.5: { label: <span style={{ color: '#999' }}>0.5</span> },
                                        1: { label: <span style={{ color: '#999' }}>1</span> }
                                      }}
                                    />
                                  </div>

                                  {sfx.reasoning && (
                                    <Text style={{ fontSize: '10px', color: '#aaa' }}>
                                      💡 {sfx.reasoning}
                                    </Text>
                                  )}
                                </Space>
                              </Card>
                            ))
                          ) : (
                            <Empty 
                              description={<Text style={{ color: '#999' }}>No sound effects</Text>}
                              image={Empty.PRESENTED_IMAGE_SIMPLE} 
                            />
                          )}
                        </Space>
                      </TabPane>
                    </Tabs>
                  </Card>
                ) : (
                  <Card 
                    size="small"
                    style={{ backgroundColor: '#333', border: '1px solid #3a3a3a' }}
                    styles={{ body: { backgroundColor: '#333' } }}
                  >
                    <Empty 
                      description={<Text style={{ color: '#999' }}>Select a panel to edit</Text>}
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                  </Card>
                )
              )}

              {/* 🆕 Epic 6: Subtitle Editor Panel */}
              {rightPanelTab === 'subtitle' && episode.subtitles && (episode.subtitles as any).files && (
            <SubtitleEditorPanel
              subtitleEntries={
                editedPanels
                  .filter(p => p.subtitleEntries && p.subtitleEntries[selectedSubtitleLang])
                  .flatMap(p => p.subtitleEntries[selectedSubtitleLang])
                  .sort((a, b) => a.startTime - b.startTime)
              }
              currentTime={currentTime}
              onSeek={handleSeek}
              onUpdateEntry={(index, entry) => {
                // Find which panel this subtitle belongs to
                let currentIndex = 0;
                for (let i = 0; i < editedPanels.length; i++) {
                  const panel = editedPanels[i];
                  const entries = panel.subtitleEntries?.[selectedSubtitleLang] || [];
                  
                  if (currentIndex + entries.length > index) {
                    // This panel contains the subtitle
                    const localIndex = index - currentIndex;
                    const updatedEntries = [...entries];
                    updatedEntries[localIndex] = entry;
                    
                    const updatedPanels = [...editedPanels];
                    updatedPanels[i] = {
                      ...panel,
                      subtitleEntries: {
                        ...panel.subtitleEntries,
                        [selectedSubtitleLang]: updatedEntries,
                      },
                    };
                    
                    setEditedPanels(updatedPanels);
                    setHasChanges(true);
                    break;
                  }
                  
                  currentIndex += entries.length;
                }
              }}
              onDeleteEntry={(index) => {
                // Find which panel this subtitle belongs to
                let currentIndex = 0;
                for (let i = 0; i < editedPanels.length; i++) {
                  const panel = editedPanels[i];
                  const entries = panel.subtitleEntries?.[selectedSubtitleLang] || [];
                  
                  if (currentIndex + entries.length > index) {
                    // This panel contains the subtitle
                    const localIndex = index - currentIndex;
                    const updatedEntries = entries.filter((_: any, idx: number) => idx !== localIndex);
                    
                    const updatedPanels = [...editedPanels];
                    updatedPanels[i] = {
                      ...panel,
                      subtitleEntries: {
                        ...panel.subtitleEntries,
                        [selectedSubtitleLang]: updatedEntries,
                      },
                    };
                    
                    setEditedPanels(updatedPanels);
                    setHasChanges(true);
                    break;
                  }
                  
                  currentIndex += entries.length;
                }
              }}
              onAddEntry={(entry) => {
                // Find which panel this time belongs to
                const targetPanel = editedPanels.find(
                  p => entry.startTime >= p.startTime && entry.startTime < p.endTime
                );
                
                if (targetPanel) {
                  const panelIndex = editedPanels.findIndex(p => p.panelId === targetPanel.panelId);
                  const existingEntries = targetPanel.subtitleEntries?.[selectedSubtitleLang] || [];
                  const updatedEntries = [...existingEntries, entry].sort((a, b) => a.startTime - b.startTime);
                  
                  const updatedPanels = [...editedPanels];
                  updatedPanels[panelIndex] = {
                    ...targetPanel,
                    subtitleEntries: {
                      ...targetPanel.subtitleEntries,
                      [selectedSubtitleLang]: updatedEntries,
                    },
                  };
                  
                  setEditedPanels(updatedPanels);
                  setHasChanges(true);
                } else {
                  message.error('Cannot add subtitle: time is outside any panel range');
                }
              }}
              language={selectedSubtitleLang}
            />
          )}
          </div>

        </div>
      </div>
    </Modal>
  );
};

export default VideoEditorModal;
